import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { MenuItem } from '@/lib/types';
import { SectionHeader } from '@/components/ui';
import {
  GripVertical,
  Plus,
  Edit2,
  Trash2,
  Check,
  ChevronDown,
  ChevronRight,
  MoveUp,
  MoveDown,
  Globe,
  Link,
  Save,
  Menu
} from 'lucide-react';

const INITIAL_MENU: MenuItem[] = [
  {
    id: 'm1',
    label: 'Home',
    slug: 'home',
    path: '/',
    is_header: true,
    order: 1,
    children: []
  },
  {
    id: 'm2',
    label: 'Explore Directory',
    slug: 'search',
    path: '/search',
    is_header: true,
    order: 2,
    children: [
      { id: 'm2_1', label: 'Cafés in City', slug: 'cat-cafe', path: '/cat/Cafe', is_header: false, order: 1 },
      { id: 'm2_2', label: 'Restaurants', slug: 'cat-restaurant', path: '/cat/Restaurant', is_header: false, order: 2 },
      { id: 'm2_3', label: 'Home Services', slug: 'cat-home-services', path: '/cat/Home%20Services', is_header: false, order: 3 },
    ]
  },
  {
    id: 'm3',
    label: 'Real Estate Hub',
    slug: 'real-estate',
    path: '/search?category=Real%20Estate',
    is_header: true,
    order: 3,
    children: [
      { id: 'm3_1', label: 'Chennai Properties', slug: 'real-estate-chennai', path: '/search/real-estate-in-chennai', is_header: false, order: 1 },
      { id: 'm3_2', label: 'Mumbai Properties', slug: 'real-estate-mumbai', path: '/search/real-estate-in-mumbai', is_header: false, order: 2 },
    ]
  },
  {
    id: 'm4',
    label: 'Top Blogs',
    slug: 'blogs',
    path: '/blogs',
    is_header: true,
    order: 4,
    children: []
  }
];

export default function MenuManagement() {
  const [menuList, setMenuList] = useState<MenuItem[]>(() => {
    try {
      const saved = localStorage.getItem('localhub_menu_config');
      return saved ? JSON.parse(saved) : INITIAL_MENU;
    } catch {
      return INITIAL_MENU;
    }
  });

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editSlug, setEditSlug] = useState('');
  const [editPath, setEditPath] = useState('');

  // New item modal
  const [showAddModal, setShowAddModal] = useState<string | null>(null); // null = top header, or parent_id for subheader
  const [newLabel, setNewLabel] = useState('');
  const [newSlug, setNewSlug] = useState('');
  const [newPath, setNewPath] = useState('');

  const [saving, setSaving] = useState(false);

  // Save changes to localStorage & DB settings table
  const saveMenuState = async (newList: MenuItem[]) => {
    setMenuList(newList);
    localStorage.setItem('localhub_menu_config', JSON.stringify(newList));
    try {
      await supabase.from('settings').upsert({
        key: 'navigation_menu',
        value: newList
      });
    } catch (e) {
      console.error(e);
    }
  };

  // Move Header items up/down
  const moveHeader = (index: number, direction: 'up' | 'down') => {
    const list = [...menuList];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= list.length) return;
    const temp = list[index];
    list[index] = list[targetIndex];
    list[targetIndex] = temp;
    saveMenuState(list);
  };

  // Move Subheader items up/down
  const moveSubItem = (headerIndex: number, subIndex: number, direction: 'up' | 'down') => {
    const list = [...menuList];
    const children = [...(list[headerIndex].children || [])];
    const targetIndex = direction === 'up' ? subIndex - 1 : subIndex + 1;
    if (targetIndex < 0 || targetIndex >= children.length) return;
    const temp = children[subIndex];
    children[subIndex] = children[targetIndex];
    children[targetIndex] = temp;
    list[headerIndex].children = children;
    saveMenuState(list);
  };

  // Start inline editing slug/label/path
  const startEdit = (item: MenuItem) => {
    setEditingId(item.id);
    setEditLabel(item.label);
    setEditSlug(item.slug);
    setEditPath(item.path);
  };

  // Confirm inline edit
  const confirmEdit = () => {
    if (!editingId) return;
    const updateRecursive = (items: MenuItem[]): MenuItem[] => {
      return items.map(item => {
        if (item.id === editingId) {
          return {
            ...item,
            label: editLabel,
            slug: editSlug.toLowerCase().replace(/\s+/g, '-'),
            path: editPath
          };
        }
        if (item.children) {
          return { ...item, children: updateRecursive(item.children) };
        }
        return item;
      });
    };
    const updated = updateRecursive(menuList);
    saveMenuState(updated);
    setEditingId(null);
  };

  // Delete menu item
  const deleteItem = (id: string) => {
    if (!confirm('Are you sure you want to delete this menu item?')) return;
    const filterRecursive = (items: MenuItem[]): MenuItem[] => {
      return items
        .filter(item => item.id !== id)
        .map(item => ({
          ...item,
          children: item.children ? filterRecursive(item.children) : []
        }));
    };
    saveMenuState(filterRecursive(menuList));
  };

  // Add new item (Header or Subheader)
  const handleAddItem = () => {
    if (!newLabel.trim()) return;
    const slug = newSlug.trim() || newLabel.toLowerCase().replace(/\s+/g, '-');
    const path = newPath.trim() || `/${slug}`;
    const newItem: MenuItem = {
      id: Math.random().toString(36).substr(2, 9),
      label: newLabel,
      slug,
      path,
      is_header: !showAddModal,
      order: 99,
      children: []
    };

    if (!showAddModal) {
      // Adding top-level header
      saveMenuState([...menuList, newItem]);
    } else {
      // Adding subheader child
      const updated = menuList.map(h => {
        if (h.id === showAddModal) {
          return {
            ...h,
            children: [...(h.children || []), newItem]
          };
        }
        return h;
      });
      saveMenuState(updated);
    }

    setNewLabel('');
    setNewSlug('');
    setNewPath('');
    setShowAddModal(null);
  };

  return (
    <div className="space-y-6 bg-white p-6 rounded-3xl min-h-screen text-slate-800">
      <SectionHeader
        title="Menu Management System"
        subtitle="Drag-and-drop hierarchy reordering, Header & Subheader nesting, manual slug & URL path editing."
      />

      <div className="flex justify-between items-center bg-slate-50 border border-slate-200 p-4 rounded-2xl">
        <div className="flex items-center gap-2">
          <Menu className="h-5 w-5 text-emerald-600" />
          <span className="text-sm font-bold text-slate-900">Header & Navigation Structure</span>
        </div>
        <button
          onClick={() => setShowAddModal('root')}
          className="bg-primary hover:bg-primary-container text-white px-4 py-2 rounded-xl text-xs font-extrabold shadow-sm transition flex items-center gap-1.5 cursor-pointer"
        >
          <Plus className="h-4 w-4" /> Add Header Navigation
        </button>
      </div>

      {/* Menu Tree List */}
      <div className="space-y-4 max-w-4xl mx-auto">
        {menuList.map((header, hIdx) => (
          <div key={header.id} className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3 shadow-xs">
            {/* Header Row */}
            <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 p-3 rounded-xl">
              <div className="flex items-center gap-3 flex-grow min-w-0">
                <GripVertical className="h-4 w-4 text-slate-400 cursor-grab shrink-0" />
                
                {editingId === header.id ? (
                  <div className="flex flex-wrap gap-2 flex-grow">
                    <input
                      type="text"
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      placeholder="Label"
                      className="px-3 py-1 border border-slate-300 rounded-lg text-xs font-bold"
                    />
                    <input
                      type="text"
                      value={editSlug}
                      onChange={(e) => setEditSlug(e.target.value)}
                      placeholder="Slug"
                      className="px-3 py-1 border border-slate-300 rounded-lg text-xs font-mono"
                    />
                    <input
                      type="text"
                      value={editPath}
                      onChange={(e) => setEditPath(e.target.value)}
                      placeholder="URL Path"
                      className="px-3 py-1 border border-slate-300 rounded-lg text-xs font-mono"
                    />
                    <button onClick={confirmEdit} className="p-1 bg-emerald-600 text-white rounded-lg"><Check className="h-4 w-4" /></button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 flex-grow min-w-0">
                    <span className="font-extrabold text-slate-900 text-sm truncate">{header.label}</span>
                    <span className="bg-slate-100 text-slate-600 font-mono text-[10px] px-2 py-0.5 rounded border border-slate-200">
                      slug: /{header.slug}
                    </span>
                    <span className="text-[11px] text-slate-400 font-mono truncate">{header.path}</span>
                  </div>
                )}
              </div>

              {/* Controls */}
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => moveHeader(hIdx, 'up')}
                  disabled={hIdx === 0}
                  className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg disabled:opacity-30"
                  title="Move Up"
                >
                  <MoveUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => moveHeader(hIdx, 'down')}
                  disabled={hIdx === menuList.length - 1}
                  className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg disabled:opacity-30"
                  title="Move Down"
                >
                  <MoveDown className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setShowAddModal(header.id)}
                  className="p-1.5 hover:bg-emerald-50 text-emerald-700 rounded-lg font-bold text-xs flex items-center gap-1"
                  title="Add Subheader item"
                >
                  <Plus className="h-3.5 w-3.5" /> Subheader
                </button>
                <button onClick={() => startEdit(header)} className="p-1.5 hover:bg-slate-100 text-slate-600 rounded-lg"><Edit2 className="h-3.5 w-3.5" /></button>
                <button onClick={() => deleteItem(header.id)} className="p-1.5 hover:bg-red-50 text-red-600 rounded-lg"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>

            {/* Subheader Children Row */}
            {header.children && header.children.length > 0 && (
              <div className="pl-6 space-y-2 border-l-2 border-slate-200 ml-3">
                {header.children.map((sub, sIdx) => (
                  <div key={sub.id} className="flex items-center justify-between gap-3 bg-white border border-slate-200 p-2.5 rounded-xl text-xs">
                    <div className="flex items-center gap-2 flex-grow min-w-0">
                      <GripVertical className="h-3.5 w-3.5 text-slate-300 cursor-grab shrink-0" />
                      
                      {editingId === sub.id ? (
                        <div className="flex flex-wrap gap-2 flex-grow">
                          <input
                            type="text"
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            className="px-2 py-1 border border-slate-300 rounded text-xs font-bold"
                          />
                          <input
                            type="text"
                            value={editSlug}
                            onChange={(e) => setEditSlug(e.target.value)}
                            className="px-2 py-1 border border-slate-300 rounded text-xs font-mono"
                          />
                          <button onClick={confirmEdit} className="p-1 bg-emerald-600 text-white rounded"><Check className="h-3.5 w-3.5" /></button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 truncate">
                          <span className="font-bold text-slate-800">{sub.label}</span>
                          <span className="text-[10px] font-mono text-slate-400">/{sub.slug}</span>
                          <span className="text-[10px] text-slate-400 font-mono truncate">{sub.path}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => moveSubItem(hIdx, sIdx, 'up')}
                        disabled={sIdx === 0}
                        className="p-1 hover:bg-slate-100 text-slate-500 rounded disabled:opacity-30"
                      >
                        <MoveUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => moveSubItem(hIdx, sIdx, 'down')}
                        disabled={sIdx === (header.children?.length ?? 0) - 1}
                        className="p-1 hover:bg-slate-100 text-slate-500 rounded disabled:opacity-30"
                      >
                        <MoveDown className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => startEdit(sub)} className="p-1 hover:bg-slate-100 text-slate-500 rounded"><Edit2 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => deleteItem(sub.id)} className="p-1 hover:bg-red-50 text-red-600 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Modal for Adding Menu Item */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-xs flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4">
            <h3 className="text-base font-extrabold text-slate-900">
              {showAddModal === 'root' ? 'Add Top Header Navigation' : 'Add Subheader Item'}
            </h3>
            
            <div className="space-y-3">
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Navigation Label *</label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Services, Pricing, Blogs"
                  className="w-full px-4 py-2 border border-slate-300 rounded-xl text-xs outline-none focus:border-primary font-semibold"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Manual Slug</label>
                <input
                  type="text"
                  value={newSlug}
                  onChange={(e) => setNewSlug(e.target.value)}
                  placeholder="e.g. services"
                  className="w-full px-4 py-2 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Target URL Path</label>
                <input
                  type="text"
                  value={newPath}
                  onChange={(e) => setNewPath(e.target.value)}
                  placeholder="e.g. /search or https://example.com"
                  className="w-full px-4 py-2 border border-slate-300 rounded-xl text-xs font-mono outline-none focus:border-primary"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowAddModal(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleAddItem}
                className="px-5 py-2 text-xs font-extrabold text-white bg-primary hover:bg-primary-container rounded-xl shadow-xs"
              >
                Add Menu Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
